<!-- Loading bar -->
@if (session.loading()) {
  <div class="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur">
    <hlm-progress />
  </div>
}

<!-- Breadcrumb header -->
<div class="border-b border-border bg-card/60 backdrop-blur-sm">
  <div [class]="barShellClass()">
    @if (statusLabel()) {
      <hlm-badge [variant]="statusBadgeVariant()">
        <span class="inline-block h-1.5 w-1.5 rounded-full bg-current"></span>
        {{ statusLabel() }}
      </hlm-badge>
    }

    <nav class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-foreground">
      <!-- Brand segment -->
      @if (brandName()) {
        <span class="flex items-center gap-1.5 text-muted-foreground">
          <hlm-icon name="lucideBuilding2" size="0.9rem" />
          <span class="truncate">{{ brandName() }}</span>
        </span>
        <hlm-icon name="lucideChevronRight" size="0.8rem" class="text-muted-foreground/50" />
      }

      <!-- Customer segment -->
      <span class="flex items-center gap-1.5">
        <hlm-icon name="lucideUser" size="0.9rem" class="text-muted-foreground" />
        <span class="truncate font-semibold text-foreground">{{ companyName() || 'Loading…' }}</span>
        @if (session.customerId() !== null) {
          <span class="font-mono-feat text-sm text-muted-foreground">({{ session.customerId() }})</span>
        }
      </span>

      <!-- UA/PS segment -->
      @if (session.selectedUserAccount() && isOnEntityRoute()) {
        <hlm-icon name="lucideChevronRight" size="0.8rem" class="text-muted-foreground/50" />
        <span class="flex items-center gap-1.5">
          <hlm-icon name="lucideUserRound" size="0.9rem" class="text-muted-foreground" />
          <span class="truncate">{{ uaBreadcrumbName() }}</span>
          <span class="font-mono-feat text-sm text-muted-foreground">{{ uaBreadcrumbIds() }}</span>
        </span>
      }

      <!-- Active tab segment -->
      @if (activeTabLabel()) {
        <hlm-icon name="lucideChevronRight" size="0.8rem" class="text-muted-foreground/50" />
        <span class="flex items-center gap-1.5 text-muted-foreground">
          <hlm-icon [name]="activeTabIcon()" size="0.9rem" />
          <span class="truncate">{{ activeTabLabel() }}</span>
        </span>
      }
    </nav>
  </div>
</div>

<!-- Sidebar + Content layout -->
<div [class]="detailShellClass()">
  <!-- Sidebar -->
  <nav
    class="sticky top-11 z-10 flex h-fit max-h-[calc(100vh-3rem)] flex-none flex-col gap-1.5 self-start overflow-y-auto transition-[width] duration-200"
    [class.w-[300px]]="sidebarExpanded()"
    [class.w-14]="!sidebarExpanded()"
  >
    <button
      type="button"
      (click)="toggleSidebar()"
      class="mb-2 ml-auto grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      [attr.aria-label]="sidebarExpanded() ? 'Collapse sidebar' : 'Expand sidebar'"
      [attr.title]="sidebarExpanded() ? 'Collapse sidebar' : 'Expand sidebar'"
    >
      <hlm-icon [name]="sidebarExpanded() ? 'lucideChevronLeft' : 'lucideMenu'" size="0.9rem" />
    </button>

    @if (sidebarExpanded()) {
      <p class="font-mono-feat mb-1 px-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
        Customer
      </p>
    }

    @for (item of customerNavItems; track item.route) {
      @if (item.disabled) {
        <div
          [class]="'flex items-center gap-3 rounded-lg px-2.5 py-2 text-base text-muted-foreground/50 cursor-not-allowed ' + (sidebarExpanded() ? '' : 'justify-center')"
          [attr.title]="!sidebarExpanded() ? item.label : null"
        >
          <hlm-icon [name]="item.icon" size="1rem" />
          @if (sidebarExpanded()) { <span class="truncate">{{ item.label }}</span> }
        </div>
      } @else {
        <a
          [routerLink]="item.route"
          routerLinkActive="bg-primary/10 text-primary font-medium"
          [routerLinkActiveOptions]="{ exact: true }"
          [class]="'group flex items-center gap-3 rounded-lg px-2.5 py-2 text-base text-foreground transition-colors hover:bg-muted ' + (sidebarExpanded() ? '' : 'justify-center')"
          [attr.title]="!sidebarExpanded() ? item.label : null"
        >
          <hlm-icon [name]="item.icon" size="1rem" class="text-muted-foreground group-[.text-primary]:text-primary" />
          @if (sidebarExpanded()) { <span class="truncate">{{ item.label }}</span> }
        </a>
      }
    }

    @if (session.userAccounts().length) {
      <div class="my-3 h-px bg-border"></div>

      @if (sidebarExpanded()) {
        <p class="font-mono-feat mb-1 px-2 text-sm uppercase tracking-[0.2em] text-muted-foreground">
          User Accounts
        </p>
        <cmt-entity-selector
          [entities]="session.userAccounts()"
          [selectedEntity]="session.selectedUserAccount()"
          label="User Account"
          icon="lucideUserRound"
          [displayFn]="uaDisplayFn"
          [nameFn]="uaNameFn"
          [uaIdFn]="uaIdColumnFn"
          [psIdFn]="psIdColumnFn"
          [statusFn]="statusFn"
          [footerLabel]="session.selectedUserAccount() ? 'Open Call Viewer' : null"
          footerIcon="lucidePhone"
          (footerAction)="openCallViewerForSelectedUa()"
          (entitySelected)="onUserAccountSelected($event)"
        />
      }

      @if (session.selectedUserAccount()) {
        <div class="mt-2 flex flex-col gap-1.5">
          @for (item of uaNavItems(); track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="bg-primary/10 text-primary font-medium"
              [class]="'group flex items-center gap-3 rounded-lg px-2.5 py-2 text-base text-foreground transition-colors hover:bg-muted ' + (sidebarExpanded() ? '' : 'justify-center')"
              [attr.title]="!sidebarExpanded() ? item.label : null"
            >
              <hlm-icon [name]="item.icon" size="1rem" class="text-muted-foreground" />
              @if (sidebarExpanded()) { <span class="truncate">{{ item.label }}</span> }
            </a>
          }

          @if (session.selectedPhoneService()) {
            @for (item of psNavItems(); track item.route) {
              <a
                [routerLink]="item.route"
                routerLinkActive="bg-primary/10 text-primary font-medium"
                [class]="'group flex items-center gap-3 rounded-lg px-2.5 py-2 text-base text-foreground transition-colors hover:bg-muted ' + (sidebarExpanded() ? '' : 'justify-center')"
                [attr.title]="!sidebarExpanded() ? item.label : null"
              >
                <hlm-icon [name]="item.icon" size="1rem" class="text-muted-foreground" />
                @if (sidebarExpanded()) { <span class="truncate">{{ item.label }}</span> }
              </a>
            }
          }

          @for (item of commNavItems(); track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="bg-primary/10 text-primary font-medium"
              [class]="'group flex items-center gap-3 rounded-lg px-2.5 py-2 text-base text-foreground transition-colors hover:bg-muted ' + (sidebarExpanded() ? '' : 'justify-center')"
              [attr.title]="!sidebarExpanded() ? item.label : null"
            >
              <hlm-icon [name]="item.icon" size="1rem" class="text-muted-foreground" />
              @if (sidebarExpanded()) { <span class="truncate">{{ item.label }}</span> }
            </a>
          }
        </div>
      }
    }
  </nav>

  <!-- Content area -->
  <!-- IMPORTANT: do NOT add overflow-auto/hidden/scroll here. The scroll
       container is the window — adding overflow on this div would hijack
       `position: sticky` for descendants (e.g. section headers like the
       Billing Details collapse toggle that should stick below the global
       titlebar while scrolling). -->
  <div class="detail-content min-h-0 min-w-0 flex-1">
    @if (session.error()) {
      <cmt-error-state
        [title]="'Failed to load customer'"
        [message]="session.error() || ''"
        [showRetry]="false"
      />
    } @else {
      <router-outlet />
    }
  </div>
</div>
